import { Direction, TileType, CatState, GameState, GamePhase } from '../types';
import { Player } from '../entities/Player';
import { Cat } from '../entities/Cat';
import { CheeseItem } from '../entities/Cheese';
import { Treat } from '../entities/PowerUp';
import { MAZE_WIDTH, MAZE_HEIGHT } from '../maps/levels';

const WALL_COLOR = '#3a506b';
const WALL_BORDER = '#5b7ea1';
const BG_COLOR = '#0f0f23';
const CHEESE_COLOR = '#f4d35e';
const POWER_CHEESE_COLOR = '#ff9f1c';
const HUD_BG = 'rgba(0,0,0,0.7)';
const HUD_TEXT = '#f0e6d3';
const MOUSE_BODY = '#b8b0a0';
const MOUSE_EAR = '#f0a0b0';
const MOUSE_EYE = '#222';
const SCARED_CAT = '#aabbdd';

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private tileSize: number;
  private hudHeight: number;
  private scale: number = 1;
  private offsetX: number = 0;
  private offsetY: number = 0;

  constructor(ctx: CanvasRenderingContext2D, tileSize: number) {
    this.ctx = ctx;
    this.tileSize = tileSize;
    this.hudHeight = 32;
  }

  resize(canvasWidth: number, canvasHeight: number): void {
    const mazePixelW = MAZE_WIDTH * this.tileSize;
    const mazePixelH = MAZE_HEIGHT * this.tileSize + this.hudHeight;
    this.scale = Math.min(canvasWidth / mazePixelW, canvasHeight / mazePixelH);
    this.offsetX = (canvasWidth - mazePixelW * this.scale) / 2;
    this.offsetY = (canvasHeight - mazePixelH * this.scale) / 2;
  }

  render(
    maze: number[][],
    player: Player,
    cats: Cat[],
    cheese: CheeseItem[],
    treat: Treat | null,
    state: GameState,
    time: number,
  ): void {
    const { ctx } = this;
    ctx.save();
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    ctx.translate(0, this.hudHeight);

    this.drawMaze(maze);
    this.drawCheese(cheese, time);
    if (treat && treat.active) this.drawTreat(treat, time);
    this.drawCats(cats, time);
    this.drawPlayer(player);

    ctx.translate(0, -this.hudHeight);
    this.drawHUD(state);

    ctx.restore();

    if (state.phase === GamePhase.Ready) {
      this.drawOverlayText('READY!', '#f4a623');
    } else if (state.phase === GamePhase.GameOver) {
      this.drawOverlayText('GAME OVER', '#e63946');
    } else if (state.phase === GamePhase.LevelComplete) {
      this.drawOverlayText('LEVEL COMPLETE!', '#5b8c5a');
    } else if (state.phase === GamePhase.Paused) {
      this.drawOverlayText('PAUSED', '#f0e6d3');
    }
  }

  private drawMaze(maze: number[][]): void {
    const { ctx, tileSize: ts } = this;
    for (let r = 0; r < MAZE_HEIGHT; r++) {
      for (let c = 0; c < MAZE_WIDTH; c++) {
        const tile = maze[r][c];
        const x = c * ts;
        const y = r * ts;
        if (tile === TileType.Wall) {
          ctx.fillStyle = WALL_COLOR;
          ctx.fillRect(x, y, ts, ts);
          ctx.strokeStyle = WALL_BORDER;
          ctx.lineWidth = 0.5;
          ctx.strokeRect(x + 0.5, y + 0.5, ts - 1, ts - 1);
        } else if (tile === TileType.CatDoor) {
          ctx.fillStyle = BG_COLOR;
          ctx.fillRect(x, y, ts, ts);
          ctx.fillStyle = '#d4a574';
          ctx.fillRect(x, y + ts * 0.35, ts, ts * 0.3);
        } else {
          ctx.fillStyle = BG_COLOR;
          ctx.fillRect(x, y, ts, ts);
        }
      }
    }
  }

  private drawCheese(cheese: CheeseItem[], time: number): void {
    const { ctx, tileSize: ts } = this;
    const pulse = 0.8 + 0.2 * Math.sin(time * 4);
    for (const c of cheese) {
      if (c.eaten) continue;
      const cx = c.col * ts + ts / 2;
      const cy = c.row * ts + ts / 2;
      if (c.type === TileType.Cheese) {
        ctx.fillStyle = CHEESE_COLOR;
        ctx.beginPath();
        ctx.arc(cx, cy, ts * 0.15, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillStyle = POWER_CHEESE_COLOR;
        ctx.beginPath();
        ctx.arc(cx, cy, ts * 0.35 * pulse, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawTreat(treat: Treat, time: number): void {
    const { ctx, tileSize: ts } = this;
    const cx = treat.col * ts + ts / 2;
    const cy = treat.row * ts + ts / 2;
    const bob = Math.sin(time * 5) * 2;

    ctx.fillStyle = '#e63946';
    ctx.beginPath();
    ctx.arc(cx, cy + bob, ts * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = `${ts * 0.4}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('T', cx, cy + bob);
  }

  private drawPlayer(player: Player): void {
    const { ctx, tileSize: ts } = this;
    const { pos, direction, animFrame, moving } = player;

    ctx.save();
    ctx.translate(pos.x, pos.y);

    let angle = 0;
    switch (direction) {
      case Direction.Right: angle = 0; break;
      case Direction.Down: angle = Math.PI / 2; break;
      case Direction.Left: angle = Math.PI; break;
      case Direction.Up: angle = -Math.PI / 2; break;
    }
    ctx.rotate(angle);

    const r = ts * 0.45;
    const mouthAngle = moving ? Math.abs(Math.sin(animFrame * 0.8)) * 0.6 : 0.15;

    // Body
    ctx.fillStyle = MOUSE_BODY;
    ctx.beginPath();
    ctx.arc(0, 0, r, mouthAngle, Math.PI * 2 - mouthAngle);
    ctx.lineTo(0, 0);
    ctx.closePath();
    ctx.fill();

    // Ears
    ctx.fillStyle = MOUSE_EAR;
    ctx.beginPath();
    ctx.arc(-r * 0.3, -r * 0.7, r * 0.35, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(-r * 0.3, r * 0.7, r * 0.35, 0, Math.PI * 2);
    ctx.fill();

    // Eye
    ctx.fillStyle = MOUSE_EYE;
    ctx.beginPath();
    ctx.arc(r * 0.2, -r * 0.3, r * 0.15, 0, Math.PI * 2);
    ctx.fill();

    // Tail
    ctx.strokeStyle = MOUSE_BODY;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-r, 0);
    ctx.quadraticCurveTo(-r * 1.5, -r * 0.5, -r * 1.8, r * 0.2);
    ctx.stroke();

    ctx.restore();
  }

  private drawCats(cats: Cat[], time: number): void {
    for (const cat of cats) {
      this.drawCat(cat, time);
    }
  }

  private drawCat(cat: Cat, time: number): void {
    const { ctx, tileSize: ts } = this;
    const { pos, state, color, animFrame } = cat;

    if (state === CatState.Fleeing) {
      this.drawDazedCat(pos.x, pos.y, ts);
      return;
    }

    ctx.save();
    ctx.translate(pos.x, pos.y);

    const r = ts * 0.45;
    const isScared = state === CatState.Scared;
    const bodyColor = isScared
      ? (cat.scaredTimer < 2 && Math.sin(time * 12) > 0 ? '#fff' : SCARED_CAT)
      : color;

    // Body
    ctx.fillStyle = bodyColor;
    ctx.beginPath();
    ctx.ellipse(0, r * 0.1, r, r * 0.9, 0, 0, Math.PI * 2);
    ctx.fill();

    // Ears
    ctx.beginPath();
    ctx.moveTo(-r * 0.7, -r * 0.5);
    ctx.lineTo(-r * 0.4, -r * 1.1);
    ctx.lineTo(-r * 0.1, -r * 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(r * 0.1, -r * 0.5);
    ctx.lineTo(r * 0.4, -r * 1.1);
    ctx.lineTo(r * 0.7, -r * 0.5);
    ctx.closePath();
    ctx.fill();

    // Eyes
    if (isScared) {
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      const ey = -r * 0.15;
      ctx.beginPath();
      ctx.moveTo(-r * 0.35, ey - 2);
      ctx.lineTo(-r * 0.15, ey + 2);
      ctx.moveTo(-r * 0.15, ey - 2);
      ctx.lineTo(-r * 0.35, ey + 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(r * 0.15, ey - 2);
      ctx.lineTo(r * 0.35, ey + 2);
      ctx.moveTo(r * 0.35, ey - 2);
      ctx.lineTo(r * 0.15, ey + 2);
      ctx.stroke();
    } else {
      ctx.fillStyle = '#ffe66d';
      ctx.beginPath();
      ctx.ellipse(-r * 0.25, -r * 0.15, r * 0.18, r * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.ellipse(r * 0.25, -r * 0.15, r * 0.18, r * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#111';
      ctx.beginPath();
      ctx.ellipse(-r * 0.25, -r * 0.15, r * 0.08, r * 0.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(r * 0.25, -r * 0.15, r * 0.08, r * 0.2, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Whiskers
    if (!isScared) {
      ctx.strokeStyle = bodyColor === '#333' ? '#888' : '#555';
      ctx.lineWidth = 0.5;
      const wy = r * 0.15;
      ctx.beginPath();
      ctx.moveTo(-r * 0.2, wy);
      ctx.lineTo(-r * 0.9, wy - 2);
      ctx.moveTo(-r * 0.2, wy + 1);
      ctx.lineTo(-r * 0.9, wy + 3);
      ctx.moveTo(r * 0.2, wy);
      ctx.lineTo(r * 0.9, wy - 2);
      ctx.moveTo(r * 0.2, wy + 1);
      ctx.lineTo(r * 0.9, wy + 3);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawDazedCat(x: number, y: number, ts: number): void {
    const { ctx } = this;
    const r = ts * 0.35;

    ctx.save();
    ctx.translate(x, y);
    ctx.globalAlpha = 0.6;

    ctx.fillStyle = '#888';
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(-r * 0.3, -r * 0.2, 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(r * 0.3, -r * 0.2, 3, 0, Math.PI * 2);
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private drawHUD(state: GameState): void {
    const { ctx, tileSize: ts } = this;
    const w = MAZE_WIDTH * ts;

    ctx.fillStyle = HUD_BG;
    ctx.fillRect(0, 0, w, this.hudHeight);

    ctx.fillStyle = HUD_TEXT;
    ctx.font = 'bold 12px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`SCORE: ${state.score}`, 8, this.hudHeight / 2);

    ctx.textAlign = 'center';
    ctx.fillText(`LEVEL ${state.level}`, w / 2, this.hudHeight / 2);

    ctx.textAlign = 'right';
    let livesStr = '';
    for (let i = 0; i < state.lives; i++) livesStr += '\u{1F42D} ';
    ctx.fillText(livesStr.trim(), w - 8, this.hudHeight / 2);
  }

  private drawOverlayText(text: string, color: string): void {
    const { ctx } = this;
    const cw = ctx.canvas.width;
    const ch = ctx.canvas.height;

    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, cw, ch);

    ctx.fillStyle = color;
    ctx.font = 'bold 32px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, cw / 2, ch / 2);
    ctx.restore();
  }

  getScale(): number {
    return this.scale;
  }

  getOffset(): { x: number; y: number } {
    return { x: this.offsetX, y: this.offsetY };
  }

  getHudHeight(): number {
    return this.hudHeight;
  }
}
